import { useCallback, useContext, useEffect, useMemo } from "react";
import Form from "react-bootstrap/Form";
import { v4 as uuidv4 } from 'uuid';
import { QuestionOption, QuestionProps } from "./Props";

import { SupportedLanguages } from "@aidkitorg/i18n/lib";
import type { BooleanExpr, TrackedObjectInfo } from '@aidkitorg/types/lib/survey';
import { wrappedFn } from "@aidkitorg/types/lib/eval";
import { CompileExpressionToJS } from "@aidkitorg/types/lib/translation/expr_to_js";
import { Dropdown } from "react-bootstrap";
import InterfaceContext from "../Context";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import { useLocalizedStrings } from "../Localization";
import { languageContent, safeParse } from "../Util";
import { fisherYatesShuffle, hash } from "../utils/orderRandomizer";
import { useMarkdown } from "../Util";


/**
 * Encapsulates the useMarkdown hook so that:
 *  - Hooks are only called at the top level of this component
 *  - Parent components can render it conditionally or in loops without risking invalid hook call errors
 *
 * @param text  The raw markdown string to render
 */
export function MarkdownLabel({ text }: { text: string }) {
  const rendered = useMarkdown(text, false, true);
  return <>{rendered}</>;
}

/**
 * Evaluates a SelectChoice's `excludeWhen` boolean expression against the
 * current applicant info, returning whether the option should be hidden.
 *
 * Fail-open: if the expression is missing, malformed, or throws at runtime,
 * we return `false` so the option STAYS VISIBLE. This avoids silently hiding
 * valid choices from users due to author error or transient missing data —
 * the trade-off being that a broken `excludeWhen` will be a no-op rather than
 * an over-eager filter. Errors are still logged via `wrappedFn`.
 */
export function shouldExcludeChoice(
  excludeWhen: BooleanExpr | undefined,
  info: Record<string, string | undefined>,
): boolean {
  if (!excludeWhen) return false;
  try {
    const expr = CompileExpressionToJS(excludeWhen);
    const wrappedResult = wrappedFn(
      "return (function(info, org, screener, view_info) { const out = " + expr + "; return out });",
      null,
      { args: [info, {}, {}, {}] },
    );
    if (!wrappedResult || (wrappedResult as { error?: unknown }).error) {
      // Compile/eval failed inside wrappedFn — fail open so the option shows.
      return false;
    }
    return !!(wrappedResult as { result: unknown }).result;
  } catch (e) {
    // Any unexpected throw (e.g. from CompileExpressionToJS) — fail open.
    console.error("shouldExcludeChoice: unexpected error evaluating excludeWhen, defaulting to visible", e);
    return false;
  }
}

export function getOptionsFromProps(props: QuestionProps) {
  // if SelectFromData, get options from source field, else use "Options (if relevant)"
  const metadata = safeParse(props["Metadata"] || '{}');
  const sourceValue = safeParse(props.info[metadata.source_field] || '""');
  if (metadata.source_field && Array.isArray(sourceValue)) {
    return sourceValue.map((i: string) => ({ 'Name': i, "English Text": i } as QuestionOption));
  } else {
    return props["Options (if relevant)"] || []
  }
}

type ChoiceType = {
  value: string,
  label: {
    [key in (typeof SupportedLanguages)[number]]: string
  } & { _trackedObjInfo?: TrackedObjectInfo, },
  excludeWhen?: BooleanExpr,
}

export function SingleSelectQuestion(props: QuestionProps) {
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const metadata = safeParse(props["Metadata"] || '{}');
  const targetField = props["Target Field"]!;
  let options = getOptionsFromProps(props);
  const propsInfo = props.info;
  const setInfoKey = useCallback(props.setInfoKey, [propsInfo]);

  const name = props["Target Field"] || "";
  const uniqueId = props.Question + '_' + targetField;

  const horizontal = (props["Additional Options"] || []).indexOf("Horizontal") !== -1;
  const no_labels = (props["Additional Options"] || []).indexOf("No Labels") !== -1;

  const regex = /\$[a-zA-Z\\_][a-zA-Z\\_0-9]+/g;

  function replacer(match: string) {
    const variable = match.slice(1).replace(/\\_/g, '_');

    // console.log("Found match:", variable, variable === "uid");

    if (variable === "uid") return props.uid;
    if (props.info[variable] !== undefined && props.info[variable] !== null) return props.info[variable];
    return match;
  }

  const marked = useModularMarkdown({
    content: props[languageContent(context.lang)] || '',
    info: props.info,
    replacer: str => str.replace(regex, replacer)
  });

  let seed: string | null = null;
  if (metadata?.component?.randomizeOrder && targetField) {
    seed = localStorage.getItem('ChoiceRandomizerSeed' + (props.uid || ''));
    if (seed === null) {
      seed = Math.random() + (props.uid || '');
      localStorage.setItem('ChoiceRandomizerSeed' + (props.uid || ''), seed);
    }
    options = fisherYatesShuffle(options, hash(0, seed), targetField);
  }

  function updateOtherValue(otherKey: string, otherValue: string) {
    props.setInfoKey(props["Target Field"] + '_' + otherKey, otherValue, otherValue.length > 0, false);
  }

  function updateValue(newValue: string): void {
    const option = options.find((o: QuestionOption) => o.Name === newValue);

    const disqualified = option && option["Terminates Application"];

    if (propsInfo[targetField] !== newValue) {
      setInfoKey(targetField, newValue, newValue.length > 0, disqualified || false);
    }

    for (const opt of options) {
      if (opt.Name === newValue) continue;
      if ((opt.Name === 'other' || opt["Other Field"]) &&
        props.info[props["Target Field"] + '_' + opt.Name]) {
        updateOtherValue(opt.Name, "");
      }
    }
  }

  async function confirmDelete() {
    const yOrN = window.confirm(L.questions.choice.are_you_sure);
    if (yOrN && yOrN === true) {
      updateValue("");
    }
  }

  if ((props["Additional Options"] || []).indexOf("Hidden") !== -1) {
    return <span></span>
  }

  function ClearAnswerDropdown(props: { targetField: string, confirmDelete: () => void }) {
    return (
      <Dropdown className="applicant-led-hidden" style={{ display: 'inline' }}>
        <Dropdown.Toggle variant="link">...</Dropdown.Toggle>
        <Dropdown.Menu>
          <Dropdown.Item key={`clear-answer-for-${targetField}`}
            onClick={() => confirmDelete()}>
            {L.questions.choice.clear_answer}
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  const questionID = useMemo(() => uuidv4(), []);

  const choiceToTrackedObjInfoMap: Record<string, TrackedObjectInfo | undefined> = {};
  const choiceToExcludeWhenMap: Record<string, BooleanExpr | undefined> = {};
  (metadata?.component?.choices as Array<ChoiceType> | undefined)?.forEach((choice) => {
    choiceToTrackedObjInfoMap[choice.value] = choice.label._trackedObjInfo;
    choiceToExcludeWhenMap[choice.value] = choice.excludeWhen;
  });

  const visibleOptions = options.filter((o: QuestionOption) => {
    return !shouldExcludeChoice(choiceToExcludeWhenMap[o.Name], propsInfo);
  });

  // If the currently-selected option becomes excluded (because excludeWhen flips
  // to true based on other answers), clear the saved value so we don't leave a
  // hidden selection behind that the user can't see or change but that still
  // affects downstream eligibility/validation. Mirrors the same cleanup in
  // MultipleSelectQuestion.
  const currentValue = propsInfo[targetField];
  useEffect(() => {
    if (!targetField || !currentValue) return;
    if (!shouldExcludeChoice(choiceToExcludeWhenMap[currentValue], propsInfo)) return;
    setInfoKey(targetField, "", false, false);
    // If the now-excluded option had a paired "other" free-text field, clear
    // that too — same pattern updateValue("") uses when switching selections.
    const excludedOption = options.find((o: QuestionOption) => o.Name === currentValue);
    if (excludedOption && (excludedOption.Name === 'other' || excludedOption["Other Field"])
      && propsInfo[targetField + '_' + excludedOption.Name]) {
      setInfoKey(targetField + '_' + excludedOption.Name, "", false, false);
    }
    // We deliberately depend on `propsInfo` so this re-runs whenever any field
    // referenced by an excludeWhen changes, not just `currentValue` itself.
  }, [currentValue, propsInfo, targetField]);

  return (
    <div className="w-full">
      <fieldset>
        <div className={horizontal ? 'hidden' : ''}>
          {!horizontal && <>
            <div id={questionID}>{marked}</div>
            <ClearAnswerDropdown targetField={targetField} confirmDelete={confirmDelete} />
          </>}
        </div>
        <div className={horizontal ? "flex flex-col sm:flex-row w-full" : ""}>
          {horizontal && <div className="flex flex-initial items-start w-full mb-3 sm:w-1/4 font-bold sm:font-normal">
            <div className={"sm:-mb-3 " + (horizontal ? "text-sm" : "")} >
              {marked}
              <ClearAnswerDropdown targetField={targetField} confirmDelete={confirmDelete} />
            </div>
          </div>}
          {visibleOptions.map((o) => {
            const fields: any[] = [];
            // If options generated from a source field show Name as label
            const label = (o as any)[languageContent(context.lang).replace('Content','Text')] || o["Name"];
            
            fields.push(<div className={horizontal ? "flex-1 flex flex-col items-start sm:items-center justify-start" : ""} key={o.Name}>
              {horizontal && <div className={"block text-center " + (no_labels ? "none hidden" : "")}>
                {label}
              </div>}
              <div className={"block " + (horizontal ? "center" : "")}>
                <Form.Check
                  type="radio"
                  className=""
                  onChange={(e: any) => props.info[name] !== o["Name"] && updateValue(o.Name)}
                  name={uniqueId}
                  key={uniqueId + '_' + o["Name"]}
                  value={o["Name"]}
                  checked={props.info[name] === o["Name"]}
                  id={uniqueId + '_' + o["Name"]}
                  label={<span className={`${context.textAlign === 'right' ? 'mr-4' : 'pl-0'}`}><MarkdownLabel text={label} /></span>}
                  aria-describedby={questionID}
                />
              </div>
            </div>);

            if ((o["Name"] === "other" || o["Other Field"]) && props.info[name] === o["Name"]) {
              fields.push(
                <Form.Group key={name + "_" + o["Name"] + '_group'}>
                  <Form.Control type="text"
                    defaultValue={props.info[props['Target Field'] + '_' + o.Name]}
                    onChange={(e: any) => updateOtherValue(o.Name, e.target.value)}
                    name={uniqueId + '_' + o.Name}
                    key={uniqueId + '_' + o.Name}
                    aria-describedby={questionID}
                  />
                </Form.Group>);
            }

            return fields;
          })}
        </div>
      </fieldset>
    </div>
  );
}